import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, Popover, Slider, Typography, IconButton } from "@mui/material";
import ColorizeIcon from "@mui/icons-material/Colorize";

export interface CustomColorPickerProps {
  value?: string;
  onChange?: (color: string) => void;
  label?: string;
  className?: string;
  sx?: any;
  error?: boolean;
  helperText?: string | React.ReactNode;
  disabled?: boolean;
  anchorOrigin?: {
    vertical: "top" | "center" | "bottom";
    horizontal: "left" | "center" | "right";
  };
  transformOrigin?: {
    vertical: "top" | "center" | "bottom";
    horizontal: "left" | "center" | "right";
  };
}

const PRESET_COLORS = [
  "#1E293B",
  "#334155",
  "#1E3A8A",
  "#2563EB",
  "#3B82F6",
  "#60A5FA",
  "#059669",
  "#D97706",
  "#8A5600",
  "#F59E0B",
  "#FFFFFF",
  "#000000",
];

// Helper: HSV to RGB
function hsvToRgb(h: number, s: number, v: number): [number, number, number] {
  const f = (n: number, k = (n + h / 60) % 6) =>
    v - v * s * Math.max(Math.min(k, 4 - k, 1), 0);
  return [
    Math.round(f(5) * 255),
    Math.round(f(3) * 255),
    Math.round(f(1) * 255),
  ];
}

// Helper: RGB to HSV
function rgbToHsv(r: number, g: number, b: number): [number, number, number] {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  const s = max === 0 ? 0 : (max - min) / max;
  const v = max;
  const d = max - min;
  if (max !== min) {
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h *= 60;
  }
  return [h, s, v];
}

// Helper: HEX to RGBA
function hexToRgba(hex: string): { r: number; g: number; b: number; a: number } {
  let clean = (hex || "#000000").replace("#", "").trim();
  if (clean.length === 3) {
    clean = clean
      .split("")
      .map((c) => c + c)
      .join("") + "FF";
  } else if (clean.length === 6) {
    clean = clean + "FF";
  } else if (clean.length >= 8) {
    clean = clean.slice(0, 8);
  } else {
    clean = "000000FF";
  }
  return {
    r: parseInt(clean.slice(0, 2), 16) || 0,
    g: parseInt(clean.slice(2, 4), 16) || 0,
    b: parseInt(clean.slice(4, 6), 16) || 0,
    a: (parseInt(clean.slice(6, 8), 16) || 255) / 255,
  };
}

// Helper: RGBA to HEX
function rgbaToHex(r: number, g: number, b: number, a = 1, force8Digits = false): string {
  const toHex = (n: number) =>
    Math.max(0, Math.min(255, Math.round(n)))
      .toString(16)
      .padStart(2, "0")
      .toUpperCase();
  if (a < 0.999 || force8Digits) {
    return `#${toHex(r)}${toHex(g)}${toHex(b)}${toHex(Math.round(a * 255))}`;
  }
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

const CustomColorPicker: React.FC<CustomColorPickerProps> = ({
  value = "#0942D3FF",
  onChange,
  label,
  className = "",
  sx = {},
  error,
  helperText,
  disabled = false,
  anchorOrigin,
  transformOrigin,
}) => {
  const isEightDigitHex = useRef(
    Boolean(value && value.replace("#", "").trim().length >= 8)
  );

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [paletteAnchorEl, setPaletteAnchorEl] = useState<HTMLElement | null>(null);
  const triggerRef = React.useRef<HTMLDivElement | null>(null);
  const paletteTriggerRef = React.useRef<HTMLDivElement | null>(null);
  const popoverActionRef = React.useRef<{ updatePosition: () => void } | null>(null);
  const paletteActionRef = React.useRef<{ updatePosition: () => void } | null>(null);

  // Keep popover positioned correctly relative to trigger even when parent Popper updates/transforms
  useEffect(() => {
    if (!anchorEl && !paletteAnchorEl) return;

    const reposition = () => {
      popoverActionRef.current?.updatePosition();
      paletteActionRef.current?.updatePosition();
    };

    reposition();
    const rafId = requestAnimationFrame(reposition);
    const timeoutId = setTimeout(reposition, 50);
    const intervalId = setInterval(reposition, 80);

    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
      clearInterval(intervalId);
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [anchorEl, paletteAnchorEl, value]);

  // Parse initial color
  const initialRgba = hexToRgba(value);
  const [hsv, setHsv] = useState<[number, number, number]>(() =>
    rgbToHsv(initialRgba.r, initialRgba.g, initialRgba.b)
  );
  const [alpha, setAlpha] = useState<number>(initialRgba.a);
  const [hexInput, setHexInput] = useState<string>(() => {
    const raw = value?.startsWith("#") ? value : `#${value}`;
    return raw.toUpperCase();
  });

  // Keep internal state updated when external prop changes
  useEffect(() => {
    if (value && value.replace("#", "").trim().length >= 8) {
      isEightDigitHex.current = true;
    }
    const parsed = hexToRgba(value);
    const newHsv = rgbToHsv(parsed.r, parsed.g, parsed.b);
    setHsv(newHsv);
    setAlpha(parsed.a);
    const raw = value?.startsWith("#") ? value : `#${value}`;
    setHexInput(raw.toUpperCase());
    // Immediately ensure Popover tracks the new position
    requestAnimationFrame(() => {
      popoverActionRef.current?.updatePosition();
    });
  }, [value]);

  const [r, g, b] = hsvToRgb(hsv[0], hsv[1], hsv[2]);
  const currentHex = rgbaToHex(r, g, b, alpha, isEightDigitHex.current);
  const pureHueColor = `hsl(${hsv[0]}, 100%, 50%)`;

  const handleTriggerClick = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    setAnchorEl(triggerRef.current || event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setPaletteAnchorEl(null);
  };

  const handlePaletteToggle = (event: React.MouseEvent<HTMLElement>) => {
    if (paletteAnchorEl) {
      setPaletteAnchorEl(null);
    } else {
      setPaletteAnchorEl(paletteTriggerRef.current || event.currentTarget);
    }
  };

  const updateColor = (newHsv: [number, number, number], newAlpha: number) => {
    setHsv(newHsv);
    setAlpha(newAlpha);
    const [nr, ng, nb] = hsvToRgb(newHsv[0], newHsv[1], newHsv[2]);
    const hex = rgbaToHex(nr, ng, nb, newAlpha, isEightDigitHex.current);
    setHexInput(hex);
    onChange?.(hex);
  };

  const handleHexInputChange = (text: string) => {
    setHexInput(text);
    const clean = text.replace("#", "").trim();
    if (clean.length === 6 || clean.length === 8) {
      const parsed = hexToRgba(clean);
      const newHsv = rgbToHsv(parsed.r, parsed.g, parsed.b);
      setHsv(newHsv);
      setAlpha(parsed.a);
      onChange?.(`#${clean.toUpperCase()}`);
    }
  };

  const handleRgbChange = (channel: "r" | "g" | "b", val: number) => {
    const nr = channel === "r" ? Math.max(0, Math.min(255, val)) : r;
    const ng = channel === "g" ? Math.max(0, Math.min(255, val)) : g;
    const nb = channel === "b" ? Math.max(0, Math.min(255, val)) : b;
    const newHsv = rgbToHsv(nr, ng, nb);
    updateColor(newHsv, alpha);
  };

  // 2D Saturation / Value Canvas interaction
  const canvasRef = useRef<HTMLDivElement>(null);
  const isDraggingCanvas = useRef(false);

  const handleCanvasMove = useCallback(
    (e: MouseEvent | React.MouseEvent) => {
      if (!canvasRef.current) return;
      const rect = canvasRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
      const y = Math.max(0, Math.min(rect.height, e.clientY - rect.top));
      const s = x / rect.width;
      const v = 1 - y / rect.height;
      updateColor([hsv[0], s, v], alpha);
    },
    [hsv, alpha]
  );

  const handleCanvasMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    isDraggingCanvas.current = true;
    handleCanvasMove(e);
    const onMouseMove = (moveEvent: MouseEvent) => {
      if (isDraggingCanvas.current) {
        moveEvent.preventDefault();
        handleCanvasMove(moveEvent);
      }
    };
    const onMouseUp = () => {
      isDraggingCanvas.current = false;
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  // EyeDropper API
  const handleEyeDropper = async () => {
    if ((window as any).EyeDropper) {
      try {
        const eyeDropper = new (window as any).EyeDropper();
        const result = await eyeDropper.open();
        if (result?.sRGBHex) {
          handleHexInputChange(result.sRGBHex);
        }
      } catch (err) {
        // user cancelled or error
      }
    }
  };

  const open = Boolean(anchorEl);
  const paletteOpen = Boolean(paletteAnchorEl);

  return (
    <Box sx={{ display: "inline-flex", flexDirection: "column", width: label ? "100%" : "auto" }}>
      {label && (
        <Typography sx={{ fontSize: 12, fontWeight: 400, mb: 0.5, color: "#334155" }}>
          {label}
        </Typography>
      )}

      {/* 1. Sidebar Swatch Trigger Button */}
      <Box
        ref={triggerRef}
        className={`color-swatch-box ${className}`}
        onClick={disabled ? undefined : handleTriggerClick}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          padding: "4px 10px",
          backgroundColor: disabled ? "#f8fafc" : "#ffffff",
          border: error ? "1px solid #ef4444" : "1px solid #d8d8d8",
          borderRadius: "var(--form-border-input-radius, 8px)",
          height: "35px",
          width: "125px",
          minWidth: "125px",
          boxSizing: "border-box",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1,
          transition: "all 0.2s ease",
          userSelect: "none",
          "&:hover": {
            borderColor: disabled
              ? "#d8d8d8"
              : "var(--action-button-background-color, #2563eb)",
          },
          ...sx,
        }}
      >
        <Box
          className="swatch-preview"
          sx={{
            width: 16,
            height: 16,
            borderRadius: "3px",
            border: "1px solid rgba(0, 0, 0, 0.15)",
            bgcolor: currentHex,
            flexShrink: 0,
          }}
        />
        <Typography
          className="swatch-hex"
          sx={{
            fontSize: "14px",
            fontWeight: 400,
            color: "#000000de",
            fontFamily: "var(--font-family-manrope, 'Manrope') !important",
            textTransform: "uppercase",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {currentHex}
        </Typography>
      </Box>

      {helperText && (
        <Typography sx={{ fontSize: 11, color: error ? "#ef4444" : "#64748b", mt: 0.5 }}>
          {helperText}
        </Typography>
      )}

      {/* 2. Main Color Popover (Screenshot 1) */}
      <Popover
        open={open}
        anchorEl={anchorEl || triggerRef.current}
        action={popoverActionRef}
        onClose={handleClose}
        disableRestoreFocus
        disableAutoFocus
        disableEnforceFocus
        anchorOrigin={
          anchorOrigin || {
            vertical: "bottom",
            horizontal: "left",
          }
        }
        transformOrigin={
          transformOrigin || {
            vertical: "top",
            horizontal: "left",
          }
        }
        PaperProps={{
          sx: {
            mt: 0.8,
            p: 1.5,
            width: 230,
            boxSizing: "border-box",
            borderRadius: "10px",
            boxShadow: "0 10px 25px rgba(0, 0, 0, 0.12)",
            border: "1px solid #e2e8f0",
            backgroundColor: "#ffffff",
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          },
        }}
      >
        {/* Presets Grid (2 rows of 6) */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(6, 1fr)",
            gap: "8px",
          }}
        >
          {PRESET_COLORS.map((preset) => {
            const isSelected =
              currentHex.slice(0, 7).toUpperCase() === preset.toUpperCase();
            return (
              <Box
                key={preset}
                onClick={() => {
                  const parsed = hexToRgba(preset);
                  const newHsv = rgbToHsv(parsed.r, parsed.g, parsed.b);
                  updateColor(newHsv, alpha);
                }}
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: "6px",
                  backgroundColor: preset,
                  cursor: "pointer",
                  border:
                    preset === "#FFFFFF"
                      ? "1px solid #d0d5dd"
                      : isSelected
                      ? "2px solid #2563eb"
                      : "1px solid rgba(0,0,0,0.1)",
                  boxShadow: isSelected
                    ? "0 0 0 2px rgba(37, 99, 235, 0.3)"
                    : "none",
                  transition: "transform 0.1s ease",
                  "&:hover": {
                    transform: "scale(1.15)",
                  },
                }}
              />
            );
          })}
        </Box>

        {/* HEX Input + Palette Color Swatch Box */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              alignItems: "center",
              border: "1px solid #d0d5dd",
              borderRadius: "6px",
              padding: "4px 8px",
              height: "32px",
              boxSizing: "border-box",
              backgroundColor: "#ffffff",
            }}
          >
            <input
              type="text"
              value={hexInput}
              onChange={(e) => handleHexInputChange(e.target.value)}
              style={{
                width: "100%",
                border: "none",
                outline: "none",
                fontSize: "13px",
                fontFamily: "var(--font-family-manrope, 'Manrope')",
                color: "#1e293b",
                fontWeight: 500,
                textTransform: "uppercase",
                background: "transparent",
                boxSizing: "border-box",
              }}
            />
          </Box>

          <Box
            ref={paletteTriggerRef}
            onClick={handlePaletteToggle}
            sx={{
              width: 28,
              height: 28,
              borderRadius: "6px",
              backgroundColor: currentHex,
              border: "1px solid #d0d5dd",
              cursor: "pointer",
              boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
              flexShrink: 0,
              transition: "transform 0.1s ease",
              "&:hover": {
                transform: "scale(1.08)",
              },
            }}
          />
        </Box>

        {/* Opacity Slider Row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 600,
              color: "#64748b",
              textTransform: "uppercase",
              fontFamily: "var(--font-family-manrope, 'Manrope') !important",
              letterSpacing: "0.5px",
              flexShrink: 0,
              width: "52px",
            }}
          >
            OPACITY
          </Typography>

          <Slider
            value={alpha}
            min={0}
            max={1}
            step={0.01}
            onChange={(_e, val) => updateColor(hsv, val as number)}
            sx={{
              flex: 1,
              mx: 1,
              height: 4,
              padding: "10px 0",
              "& .MuiSlider-rail": {
                backgroundColor: "#e2e8f0",
                opacity: 1,
                height: 4,
                borderRadius: "9999px",
              },
              "& .MuiSlider-track": {
                backgroundColor: "var(--action-button-background-color, #2563eb)",
                border: "none",
                height: 4,
                borderRadius: "9999px",
              },
              "& .MuiSlider-thumb": {
                width: 12,
                height: 12,
                backgroundColor: "var(--action-button-background-color, #2563eb)",
                border: "none",
                boxShadow: "none",
                "&:hover, &.Mui-focusVisible": {
                  boxShadow: "0 0 0 4px rgba(37, 99, 235, 0.16)",
                },
              },
            }}
          />

          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 500,
              color: "#334155",
              width: "36px",
              textAlign: "right",
              flexShrink: 0,
              fontFamily: "var(--font-family-manrope, 'Manrope') !important",
            }}
          >
            {Math.round(alpha * 100)}%
          </Typography>
        </Box>
      </Popover>

      {/* 3. Detailed Color Palette Popover (Screenshot 2) */}
      <Popover
        open={paletteOpen}
        anchorEl={paletteAnchorEl || paletteTriggerRef.current}
        action={paletteActionRef}
        onClose={() => setPaletteAnchorEl(null)}
        disableRestoreFocus
        disableAutoFocus
        disableEnforceFocus
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            ml: 1,
            p: 1.8,
            width: 260,
            boxSizing: "border-box",
            borderRadius: "10px",
            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.15)",
            border: "1px solid #e2e8f0",
            backgroundColor: "#ffffff",
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          },
        }}
      >
        {/* 2D Saturation / Value Gradient Canvas Area */}
        <Box
          ref={canvasRef}
          onMouseDown={handleCanvasMouseDown}
          sx={{
            width: "100%",
            height: 140,
            borderRadius: "6px",
            position: "relative",
            backgroundColor: pureHueColor,
            backgroundImage:
              "linear-gradient(to right, #fff, transparent), linear-gradient(to top, #000, transparent)",
            cursor: "crosshair",
            overflow: "hidden",
            boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.1)",
          }}
        >
          {/* Thumb marker */}
          <Box
            sx={{
              position: "absolute",
              left: `${hsv[1] * 100}%`,
              top: `${(1 - hsv[2]) * 100}%`,
              width: 14,
              height: 14,
              borderRadius: "50%",
              border: "2px solid #ffffff",
              backgroundColor: currentHex,
              boxShadow: "0 0 4px rgba(0,0,0,0.5)",
              transform: "translate(-50%, -50%)",
              pointerEvents: "none",
            }}
          />
        </Box>

        {/* Hue & Preview Row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.2,
          }}
        >
          {/* EyeDropper */}
          <IconButton
            size="small"
            onClick={handleEyeDropper}
            sx={{
              p: 0.5,
              border: "1px solid #e2e8f0",
              borderRadius: "6px",
              color: "#64748b",
              "&:hover": {
                bgcolor: "#f1f5f9",
                color: "#1e293b",
              },
            }}
          >
            <ColorizeIcon sx={{ fontSize: 16 }} />
          </IconButton>

          {/* Color Preview Circle */}
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              backgroundColor: currentHex,
              border: "1px solid #d0d5dd",
              flexShrink: 0,
            }}
          />

          {/* Rainbow Hue Slider */}
          <Box sx={{ flex: 1, position: "relative", display: "flex", alignItems: "center" }}>
            <Slider
              value={hsv[0]}
              min={0}
              max={360}
              step={1}
              onChange={(_e, val) => updateColor([val as number, hsv[1], hsv[2]], alpha)}
              sx={{
                padding: "8px 0",
                height: 10,
                "& .MuiSlider-rail": {
                  background:
                    "linear-gradient(to right, #ff0000 0%, #ffff00 17%, #00ff00 33%, #00ffff 50%, #0000ff 67%, #ff00ff 83%, #ff0000 100%)",
                  opacity: 1,
                  height: 10,
                  borderRadius: "5px",
                },
                "& .MuiSlider-track": {
                  border: "none",
                  backgroundColor: "transparent",
                },
                "& .MuiSlider-thumb": {
                  width: 14,
                  height: 14,
                  backgroundColor: "#ffffff",
                  border: "2px solid #2563eb",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                  "&:hover, &.Mui-focusVisible": {
                    boxShadow: "0 0 0 4px rgba(37, 99, 235, 0.2)",
                  },
                },
              }}
            />
          </Box>
        </Box>

        {/* RGB Inputs Row */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 1,
            pt: 0.5,
          }}
        >
          {/* R */}
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.3 }}>
            <input
              type="number"
              value={r}
              min={0}
              max={255}
              onChange={(e) => handleRgbChange("r", Number(e.target.value))}
              style={{
                width: "100%",
                padding: "4px 2px",
                textAlign: "center",
                border: "1px solid #d0d5dd",
                borderRadius: "4px",
                fontSize: "12px",
                fontFamily: "var(--font-family-manrope, 'Manrope')",
                color: "#1e293b",
                outline: "none",
              }}
            />
            <Typography sx={{ fontSize: "11px", color: "#64748b", fontWeight: 500 }}>
              R
            </Typography>
          </Box>

          {/* G */}
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.3 }}>
            <input
              type="number"
              value={g}
              min={0}
              max={255}
              onChange={(e) => handleRgbChange("g", Number(e.target.value))}
              style={{
                width: "100%",
                padding: "4px 2px",
                textAlign: "center",
                border: "1px solid #d0d5dd",
                borderRadius: "4px",
                fontSize: "12px",
                fontFamily: "var(--font-family-manrope, 'Manrope')",
                color: "#1e293b",
                outline: "none",
              }}
            />
            <Typography sx={{ fontSize: "11px", color: "#64748b", fontWeight: 500 }}>
              G
            </Typography>
          </Box>

          {/* B */}
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.3 }}>
            <input
              type="number"
              value={b}
              min={0}
              max={255}
              onChange={(e) => handleRgbChange("b", Number(e.target.value))}
              style={{
                width: "100%",
                padding: "4px 2px",
                textAlign: "center",
                border: "1px solid #d0d5dd",
                borderRadius: "4px",
                fontSize: "12px",
                fontFamily: "var(--font-family-manrope, 'Manrope')",
                color: "#1e293b",
                outline: "none",
              }}
            />
            <Typography sx={{ fontSize: "11px", color: "#64748b", fontWeight: 500 }}>
              B
            </Typography>
          </Box>

          {/* HEX / Alpha */}
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.3 }}>
            <input
              type="text"
              value={`${Math.round(alpha * 100)}%`}
              readOnly
              style={{
                width: "100%",
                padding: "4px 2px",
                textAlign: "center",
                border: "1px solid #d0d5dd",
                borderRadius: "4px",
                fontSize: "12px",
                fontFamily: "var(--font-family-manrope, 'Manrope')",
                color: "#1e293b",
                outline: "none",
                backgroundColor: "#f8fafc",
              }}
            />
            <Typography sx={{ fontSize: "11px", color: "#64748b", fontWeight: 500 }}>
              A
            </Typography>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default CustomColorPicker;
